import { useState, useEffect } from 'react';
import { Plus, X, DownloadSimple, FileXls } from '@phosphor-icons/react';
import api from '../api';
import SmartPicker from '../components/SmartPicker';
import DraftRestoreBanner from '../components/DraftRestoreBanner';
import { useWorkflowDraft } from '../hooks/useWorkflowDraft';

const fc = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const INP = {
  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
  padding: '9px 12px', color: 'hsl(0,0%,90%)',
  fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};
const LBL = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)',
  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, display: 'block',
};

function POChip({ status }) {
  const map = { draft: 'chip-neutral', approved: 'chip-info', partial_grn: 'chip-warning', closed: 'chip-active' };
  return <span className={map[status] || 'chip-neutral'}>{status?.replace('_', ' ').toUpperCase()}</span>;
}
function GRNChip({ status }) {
  const map = { pending: 'chip-neutral', partial: 'chip-warning', received: 'chip-active' };
  return <span className={map[status] || 'chip-neutral'}>{status?.toUpperCase()}</span>;
}

function CreatePOModal({ onClose, onSaved, prefill, hasDraft, restoreDraft, discardDraft, clearDraft }) {
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierType, setSupplierType] = useState('vendor');
  const [items, setItems] = useState([{ item_name: '', item_code: '', qty: '', unit: 'pcs', unit_price: '', total: 0 }]);
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.vendor_id) { setSupplierId(prefill.vendor_id); setSupplierType('vendor'); }
    if (prefill.vendor_name) setSupplierName(prefill.vendor_name);
  }, [prefill]);

  const updateItem = (idx, k, v) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const updated = { ...it, [k]: v };
      updated.total = (parseFloat(updated.qty) || 0) * (parseFloat(updated.unit_price) || 0);
      return updated;
    }));
  };
  const addItem = () => setItems(prev => [...prev, { item_name: '', item_code: '', qty: '', unit: 'pcs', unit_price: '', total: 0 }]);
  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx));
  const totalAmt = items.reduce((s, it) => s + (it.total || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/purchase-orders', {
        vendor_id: supplierId,
        vendor_name: supplierName,
        supplier_type: supplierType,
        items: items.map(it => ({
          item_name: it.item_name, item_code: it.item_code,
          qty: parseFloat(it.qty) || 0, unit: it.unit,
          unit_price: parseFloat(it.unit_price) || 0, total: it.total,
        })),
        expected_date: expectedDate, notes,
      });
      clearDraft && clearDraft();
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 640, padding: 24, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>Create Purchase Order</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <DraftRestoreBanner hasDraft={hasDraft} onRestore={restoreDraft} onDiscard={discardDraft} />
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={LBL}>Type</label>
              <select value={supplierType} onChange={e => { setSupplierType(e.target.value); setSupplierId(''); setSupplierName(''); }}
                style={{ ...INP, appearance: 'auto' }}>
                <option value="vendor">Vendor</option>
                <option value="service">Service Provider</option>
              </select>
            </div>
            <div>
              <label style={LBL}>Supplier *</label>
              <SmartPicker
                endpoint={supplierType === 'vendor' ? '/vendors' : '/service-providers'}
                params={{ status: 'active' }}
                labelKey="name"
                searchKeys={['name', 'code']}
                placeholder={supplierType === 'vendor' ? 'Search vendor...' : 'Search service provider...'}
                value={supplierName}
                onSelect={(s) => { setSupplierId(s.id); setSupplierName(s.name); }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={LBL}>Expected Delivery Date *</label>
            <input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)}
              required style={{ ...INP, width: 200 }} />
          </div>

          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={LBL}>Items</span>
              <button type="button" onClick={addItem} className="btn-ghost" style={{ fontSize: 11 }}>+ Add Item</button>
            </div>
            <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 1fr 24px',
                gap: 1, background: 'hsl(0,0%,10%)', padding: '6px 8px' }}>
                {['Item Name','Code','Qty','Unit','Unit Price',''].map(h => (
                  <span key={h} style={{ ...LBL, marginBottom: 0 }}>{h}</span>
                ))}
              </div>
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 1fr 24px',
                  gap: 1, padding: '4px 8px', borderTop: '1px solid hsl(0,0%,12%)' }}>
                  <input value={it.item_name} onChange={e => updateItem(idx,'item_name',e.target.value)}
                    placeholder="Item name" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                  <input value={it.item_code} onChange={e => updateItem(idx,'item_code',e.target.value)}
                    placeholder="Code" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                  <input value={it.qty} onChange={e => updateItem(idx,'qty',e.target.value)}
                    type="number" min="0" placeholder="0" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                  <select value={it.unit} onChange={e => updateItem(idx,'unit',e.target.value)}
                    style={{ ...INP, padding: '6px 4px', fontSize: 11, appearance: 'auto' }}>
                    {['pcs','kg','ltr','mtr','box','set'].map(u => <option key={u}>{u}</option>)}
                  </select>
                  <input value={it.unit_price} onChange={e => updateItem(idx,'unit_price',e.target.value)}
                    type="number" min="0" placeholder="0.00" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                  <button type="button" onClick={() => removeItem(idx)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,40%)' }}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'right', padding: '8px 8px 0',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(75,100%,50%)' }}>
              Total: {fc(totalAmt)}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={LBL}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ ...INP, resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving || !supplierId} className="btn-primary">
              {saving ? 'Creating...' : 'Create PO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateGRNModal({ pos, onClose, onSaved }) {
  const [poId, setPoId] = useState('');
  const [poNo, setPoNo] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const pendingPos = pos.filter(p => p.grn_status === 'pending' || p.grn_status === 'partial');

  const handlePoSelect = (e) => {
    const po = pendingPos.find(p => p.id === e.target.value);
    if (!po) return;
    setPoId(po.id); setPoNo(po.po_no); setVendorName(po.vendor_name);
    setItems((po.items || []).map(it => ({
      item_name: it.item_name, item_code: it.item_code || '',
      received_qty: String(it.qty || ''), unit: it.unit || 'pcs', batch_no: '',
    })));
  };

  const updateItem = (idx, k, v) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/grn', {
        po_id: poId, po_no: poNo, vendor_name: vendorName, notes,
        items: items.map(it => ({ ...it, received_qty: parseFloat(it.received_qty) || 0 })),
      });
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,20%)',
        width: '100%', maxWidth: 580, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase' }}>Receive Goods (GRN)</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={LBL}>Select Purchase Order *</label>
            <select value={poId} onChange={handlePoSelect} required
              style={{ ...INP, appearance: 'auto' }}>
              <option value="">— Select PO —</option>
              {pendingPos.map(p => (
                <option key={p.id} value={p.id}>{p.po_no} — {p.vendor_name}</option>
              ))}
            </select>
          </div>

          {poId && items.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <span style={LBL}>Items Received</span>
              <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 1fr',
                  gap: 1, background: 'hsl(0,0%,10%)', padding: '6px 8px' }}>
                  {['Item Name','Code','Recv Qty','Unit','Batch No'].map(h => (
                    <span key={h} style={{ ...LBL, marginBottom: 0 }}>{h}</span>
                  ))}
                </div>
                {items.map((it, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 1fr',
                    gap: 1, padding: '4px 8px', borderTop: '1px solid hsl(0,0%,12%)' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                      color: 'hsl(0,0%,70%)', padding: '6px 0' }}>{it.item_name}</span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                      color: 'hsl(0,0%,50%)', padding: '6px 0' }}>{it.item_code}</span>
                    <input value={it.received_qty} onChange={e => updateItem(idx,'received_qty',e.target.value)}
                      type="number" min="0" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                      color: 'hsl(0,0%,50%)', padding: '6px 0' }}>{it.unit}</span>
                    <input value={it.batch_no} onChange={e => updateItem(idx,'batch_no',e.target.value)}
                      placeholder="Batch" style={{ ...INP, padding: '6px 8px', fontSize: 11 }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={LBL}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ ...INP, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={saving || !poId} className="btn-primary">
              {saving ? 'Saving...' : 'Record GRN'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PurchasingPage() {
  const [tab, setTab] = useState('pos');
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPO, setShowPO] = useState(false);
  const [showGRN, setShowGRN] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } = useWorkflowDraft('procurement', 'create_po');

  const load = async () => {
    setLoading(true);
    try {
      const [pr, gr] = await Promise.all([api.get('/purchase-orders'), api.get('/grn')]);
      setPos(pr.data); setGrns(gr.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updatePoStatus = async (id, status) => {
    try {
      await api.put(`/purchase-orders/${id}/status`, { status });
      await load();
    } catch (err) { console.error(err); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/procurement/export/excel', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `ARIA_Procurement_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
    setExporting(false);
  };

  const TABS = [
    { key: 'pos', label: 'Purchase Orders' },
    { key: 'grns', label: 'GRN' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
            Purchasing
          </h1>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)', marginTop: 4 }}>
            Purchase orders and goods receipt
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleExport} disabled={exporting} className="btn-ghost"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <FileXls size={14} />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button onClick={() => setShowGRN(true)} className="btn-ghost"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <DownloadSimple size={14} />
            Receive Goods
          </button>
          <button onClick={() => setShowPO(true)} className="btn-primary"
            data-testid="create-po-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <Plus size={14} />
            Create PO
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid hsl(0,0%,14%)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '8px 20px', background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: tab === t.key ? 'hsl(75,100%,50%)' : 'hsl(0,0%,45%)',
              borderBottom: tab === t.key ? '2px solid hsl(75,100%,50%)' : '2px solid transparent',
              marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          color: 'hsl(0,0%,40%)', padding: 40, textAlign: 'center' }}>Loading...</div>
      ) : tab === 'pos' ? (
        <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 120px 100px 180px',
            background: 'hsl(0,0%,8%)', padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,14%)' }}>
            {['PO No','Vendor','Amount','Status','GRN','Actions'].map(h => (
              <span key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</span>
            ))}
          </div>
          {pos.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: 'hsl(0,0%,35%)' }}>No purchase orders yet</div>
          ) : pos.map(po => (
            <div key={po.id} data-testid="po-row"
              style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 120px 100px 180px',
                padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,10%)', alignItems: 'center' }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(75,100%,50%)' }}>{po.po_no}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(0,0%,80%)' }}>{po.vendor_name}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(0,0%,70%)' }}>{fc(po.total_amount)}</span>
              <POChip status={po.status} />
              <GRNChip status={po.grn_status} />
              <div style={{ display: 'flex', gap: 6 }}>
                {po.status === 'draft' && (
                  <button onClick={() => updatePoStatus(po.id, 'approved')}
                    className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }}>Approve</button>
                )}
                {po.status === 'approved' && (
                  <button onClick={() => updatePoStatus(po.id, 'closed')}
                    className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }}>Close</button>
                )}
                {['draft','approved'].includes(po.status) && (
                  <button onClick={() => updatePoStatus(po.id, 'rejected')}
                    className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px',
                    color: 'hsl(0,60%,55%)' }}>Reject</button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ border: '1px solid hsl(0,0%,14%)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr 100px 1fr',
            background: 'hsl(0,0%,8%)', padding: '8px 12px', borderBottom: '1px solid hsl(0,0%,14%)' }}>
            {['GRN No','PO No','Vendor','Status','Date'].map(h => (
              <span key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</span>
            ))}
          </div>
          {grns.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: 'hsl(0,0%,35%)' }}>No GRNs recorded yet</div>
          ) : grns.map(g => (
            <div key={g.id} data-testid="grn-row"
              style={{ display: 'grid', gridTemplateColumns: '120px 120px 1fr 100px 1fr',
                padding: '10px 12px', borderBottom: '1px solid hsl(0,0%,10%)', alignItems: 'center' }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(75,100%,50%)' }}>{g.grn_no}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(0,0%,60%)' }}>{g.po_no}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(0,0%,80%)' }}>{g.vendor_name}</span>
              <GRNChip status={g.status} />
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: 'hsl(0,0%,45%)' }}>{g.created_at?.slice(0,10)}</span>
            </div>
          ))}
        </div>
      )}

      {showPO && (
        <CreatePOModal
          onClose={() => setShowPO(false)}
          onSaved={() => { setShowPO(false); load(); }}
          prefill={prefill} hasDraft={hasDraft}
          restoreDraft={restoreDraft} discardDraft={discardDraft} clearDraft={clearDraft}
        />
      )}
      {showGRN && (
        <CreateGRNModal
          pos={pos}
          onClose={() => setShowGRN(false)}
          onSaved={() => { setShowGRN(false); load(); }}
        />
      )}
    </div>
  );
}
